import { useEffect, useState } from 'react';
import styled from 'styled-components';
import useLocalStorageListener from '../../util/useLocalStorageListener';
import { useSessionContext } from '../../authentication/session';
import {
  isLoggedOutKey,
  setSessionTimeout,
  useAuth,
  useIsAuth,
  useUser,
} from '../../authentication';
import { FlexColumn } from '../Flex';
import { AlertModalTypeEnum, useAlertModal } from '.';

const TIMEOUT_SECONDS = 60;

const Content = styled(FlexColumn)`${({ theme }) => `
  gap: ${theme.padding[2]};
`}`;

const Title = styled.p`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[2]};
`}`;

const Subtitle = styled.p`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.semibold};
  font-size: ${theme.fontSize[2]};
`}`;

const Clock = styled.span`${({ theme }) => `
  color: ${theme.colours.neutral[2]};
  font-weight: ${theme.fontWeight.bold};
  font-size: ${theme.fontSize[2]};
`}`;

const getNow = () => Math.ceil(Date.now() / 1000);

export default function SessionTimeoutAlert() {
  const [closedByUser, setClosedByUser] = useState(false);
  const [ignoredByUser, setIgnoredByUser] = useState(false);
  const { setAlertModalProps, showAlertModal, closeAlertModal } = useAlertModal();
  const { deleteUser } = useUser();

  useLocalStorageListener(isLoggedOutKey, (newValue) => {
    if (newValue === 'true') {
      deleteUser();
      closeAlertModal();
    }
  });

  const isAuth = useIsAuth();
  const { exp } = useSessionContext();

  const [timeout, setTimeout] = useState(exp - getNow());

  useEffect(() => {
    setIgnoredByUser(false);

    const timer = setInterval(() => {
      setTimeout(exp - getNow());
    }, 1000);

    return () => {
      clearTimeout(timer);
    };
  }, [exp]);

  const { refresh, logout } = useAuth();
  useEffect(() => {
    if (exp === 0 || !isAuth) return;

    if (timeout < 0) {
      setSessionTimeout(true);
      logout();
      closeAlertModal();
      return;
    }

    if (!closedByUser && !ignoredByUser && timeout < TIMEOUT_SECONDS) {
      setAlertModalProps({
        isOpen: true,
        title: 'Session timeout',
        confirmLabel: 'Continue session',
        cancelLabel: 'Log out',
        closeOnConfirm: false,
        type: AlertModalTypeEnum.WARNING_REVERSIBLE,
        content: (
          <Content>
            <Title>Your session is due to expire due to inactivity.</Title>
            <Subtitle>
              You will be logged out in
              {' '}
              <Clock>{`00:${timeout.toString().padStart(2, '0')}`}</Clock>
              {' '}
              seconds
            </Subtitle>
          </Content>
        ),
        onConfirm() {
          refresh().then(() => {
            closeAlertModal();
          });
        },
        onClose() {
          setClosedByUser(true);
          closeAlertModal();
          logout();
        },
        xClicked() {
          setIgnoredByUser(true);
          closeAlertModal();
        },
      });

      showAlertModal();
    }

    if (timeout > TIMEOUT_SECONDS) {
      closeAlertModal();
    }
  }, [timeout]);

  return (<></>);
}
